import type { ReactNode } from "react";
import { useT } from "../i18n/hooks.ts";
import { AddElementIcon } from "./AddElementIcon.tsx";
import { AddJunctionIcon } from "./AddJunctionIcon.tsx";
import { AddOutfallIcon } from "./AddOutfallIcon.tsx";
import { AddStorageIcon } from "./AddStorageIcon.tsx";
import { AddDividerIcon } from "./AddDividerIcon.tsx";
import { AddRaingageIcon } from "./AddRaingageIcon.tsx";
import { AddConduitIcon } from "./AddConduitIcon.tsx";
import { AddPumpIcon } from "./AddPumpIcon.tsx";
import { AddOrificeIcon } from "./AddOrificeIcon.tsx";
import { AddWeirIcon } from "./AddWeirIcon.tsx";
import { AddOutletIcon } from "./AddOutletIcon.tsx";
import { AddSubcatchmentIcon } from "./AddSubcatchmentIcon.tsx";
import { DropdownMenu, DropdownMenuItem } from "./DropdownMenu.tsx";

type AddTool =
  | "add-junction"
  | "add-outfall"
  | "add-storage"
  | "add-divider"
  | "add-raingage"
  | "add-conduit"
  | "add-pump"
  | "add-orifice"
  | "add-weir"
  | "add-outlet"
  | "add-subcatchment";

export function AddElementMenu({
  mode,
  onSelectTool,
  open,
  onToggle,
  onClose,
}: {
  mode: string;
  onSelectTool: (tool: AddTool) => void;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
}) {
  const t = useT();

  const nodeTools: { key: AddTool; label: string; icon: ReactNode }[] = [
    { key: "add-junction", label: t("toolbar.addJunction"), icon: <AddJunctionIcon /> },
    { key: "add-outfall", label: t("toolbar.addOutfall"), icon: <AddOutfallIcon /> },
    { key: "add-storage", label: t("toolbar.addStorage"), icon: <AddStorageIcon /> },
    { key: "add-divider", label: t("toolbar.addDivider"), icon: <AddDividerIcon /> },
  ];
  const linkTools: { key: AddTool; label: string; icon: ReactNode }[] = [
    { key: "add-conduit", label: t("toolbar.addConduit"), icon: <AddConduitIcon /> },
    { key: "add-pump", label: t("toolbar.addPump"), icon: <AddPumpIcon /> },
    { key: "add-orifice", label: t("toolbar.addOrifice"), icon: <AddOrificeIcon /> },
    { key: "add-weir", label: t("toolbar.addWeir"), icon: <AddWeirIcon /> },
    { key: "add-outlet", label: t("toolbar.addOutlet"), icon: <AddOutletIcon /> },
  ];
  const hydrologyTools: { key: AddTool; label: string; icon: ReactNode }[] = [
    { key: "add-raingage", label: t("toolbar.addRaingage"), icon: <AddRaingageIcon /> },
    { key: "add-subcatchment", label: t("toolbar.addSubcatchment"), icon: <AddSubcatchmentIcon /> },
  ];
  const activeTool = [...nodeTools, ...linkTools, ...hydrologyTools].find(
    (tool) => tool.key === mode
  );

  function toolButton(tool: { key: AddTool; label: string; icon: ReactNode }) {
    return (
      <DropdownMenuItem
        key={tool.key}
        icon={tool.icon}
        label={tool.label}
        active={tool.key === mode}
        onClick={() => {
          onClose();
          onSelectTool(tool.key);
        }}
      />
    );
  }

  return (
    <DropdownMenu
      open={open}
      onToggle={onToggle}
      onClose={onClose}
      // See SelectionToolsMenu.tsx's own comment — same rail-trigger fix.
      align="left"
      triggerClassName={
        activeTool
          ? "tool-rail-button tool-rail-button-active dropdown-trigger"
          : "tool-rail-button dropdown-trigger"
      }
      triggerLabel={t("toolbar.addElement")}
      triggerTitle={activeTool ? activeTool.label : t("toolbar.addElement")}
      triggerIcon={<AddElementIcon />}
    >
      <span className="menu-popover-group-label">{t("elementBrowser.nodes")}</span>
      {nodeTools.map(toolButton)}
      <span className="menu-popover-group-label">{t("elementBrowser.links")}</span>
      {linkTools.map(toolButton)}
      {/* Audit finding: Pluviómetro/Subcuenca used to hang from the
          bottom of the menu with no header, orphaned after the NODES and
          LINKS groups. Same header treatment as the other two so the
          menu reads as three consistent groups instead of two-plus-a-
          tail. The earlier "single-item categories don't need a header"
          reasoning (in `.menu-popover-group-label`'s own CSS comment,
          now updated) held for one item per group but this group has
          two — a shared HYDROLOGY header explains WHY they sit
          together. */}
      <span className="menu-popover-group-label">{t("elementBrowser.hydrology")}</span>
      {hydrologyTools.map(toolButton)}
    </DropdownMenu>
  );
}
